import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, MapPin, Search, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCityImage } from "@/data/cityImages";

const topCities = [
  { name: "Amsterdam", slug: "amsterdam" },
  { name: "Rotterdam", slug: "rotterdam" },
  { name: "Den Haag", slug: "den-haag" },
  { name: "Utrecht", slug: "utrecht" },
];

const TopCompaniesSection = () => {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const { data: companyCounts } = useQuery({
    queryKey: ["company-counts-per-city"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("public_company_profiles")
        .select("city")
        .eq("status", "active");
      if (error) throw error;

      const counts: Record<string, number> = {};
      (data || []).forEach((c: any) => {
        const city = (c.city || "").toLowerCase();
        counts[city] = (counts[city] || 0) + 1;
      });
      return counts;
    },
    staleTime: 10 * 60 * 1000,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const slug = search.trim().toLowerCase().replace(/\s+/g, "-");
    if (slug) {
      navigate(`/beste-verhuisbedrijven/${slug}`);
    }
  };

  return (
    <section className="container mx-auto px-4 py-16 md:py-20">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            Geverifieerde partners
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            Vind de beste verhuisbedrijven bij jou in de buurt
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Op zoek naar de beste verhuisbedrijven in Nederland? Blader en vergelijk verhuizers in jouw stad.
          </p>
        </div>

        {/* City cards grid — Sirelo style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {topCities.map((city) => {
            const imageSrc = getCityImage(city.slug, city.name);

            return (
              <Link
                key={city.slug}
                to={`/beste-verhuisbedrijven/${city.slug}`}
                className="group relative isolate overflow-hidden rounded-2xl min-h-[180px] hover:shadow-lg transition-all duration-300"
              >
                <img
                  src={imageSrc}
                  alt={`Stadsbeeld van ${city.name}`}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  width={1024}
                  height={768}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                <div className="relative flex h-full flex-col justify-end p-4">
                  <p className="text-sm text-white/90 font-medium">
                    Top 10 verhuisbedrijven in
                  </p>
                  <div className="flex items-center gap-1">
                    <p className="text-xl font-bold text-white">
                      {city.name}
                    </p>
                    <ArrowRight className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="max-w-lg mx-auto">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Zoek op stad..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 rounded-xl"
              />
            </div>
            <Button type="submit" size="lg" className="h-12 rounded-xl px-6">
              Zoeken
            </Button>
          </div>
        </form>

        <div className="text-center mt-6">
          <Link 
            to="/verhuisbedrijven" 
            className="text-sm text-primary font-medium hover:underline inline-flex items-center gap-1"
          >
            Bekijk alle verhuisbedrijven in Nederland
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TopCompaniesSection;
